<template>
  <div id="app">
    <RefreshTokenText v-if="initialRefreshTokenLoading" />
    <DownloadingMediaAssetLoader v-else-if="isSynchronizingMediaAssets || isLoadingFonts" />
    <router-view v-else />

    <PlayerModal v-if="showPlayerModal" />
  </div>
</template>

<script>
  import RefreshTokenText from '@/components/RefreshTokenText.vue';
  import PlayerModal from '@/components/player/PlayerModal.vue';
  import DownloadingMediaAssetLoader from '@/components/common/ui/dowloadingMediaAssetLoader/DownloadingMediaAssetLoader.vue';
  import { loadPicflowFonts } from '@/utils/loadPicflowFonts/loadPicflowFonts';
  import PicflowSentryPlugin from '@/plugins/picflowSentryPlugin';
  import { isLoggedIn } from '@/services/auth';
  import config from '@/config';
  import {
    SESSION_TOKEN_ENUM,
    PLAYER_ORIENTATION,
    SCREEN_ORIENTATION,
    SCREEN_ID_ENUM,
    SCREEN_LAYOUT_ENUM,
    WATCHDOG_ENABLED,
    EXPECTED_LOGOUT,
  } from './constants';
  import {
    reloadPage,
    setUniqueScreenCode,
    sleepIfNetworkSpikeRange,
    clearStorage,
  } from '@/helpers/utils';
  import { FileSynchronizationManager } from '@/utils/internalFileStorage/fileSynchronizationManager';
  import { downloadVideosForAndroid } from '@/utils/internalFileStorage/androidVideoDownloader';
  import { downloadMediaForTizen } from '@/utils/internalFileStorage/tizenVideoDownloader';
  import { tryAndroidInterfaceInit } from '@/helpers/utils';
  import { getWebOsDeviceInfo, webOSAppInitialization } from '@/utils/webOS';
  import { getTizenDeviceInfo, restartTizenScreen, changeTizenOrientation } from '@/utils/tizen';
  import { refreshAuthToken } from '@/api/login';
  import {
    getScreenEvents,
    saveScreenEvents,
    saveDeviceInfo,
    sendScreenRefreshEvent,
  } from '@/api/screenEvent';
  import localforage from 'localforage';
  import { fetchScreenLayout } from '@/services/layout';
  import { SET_PLAYER_ROTATION } from '@/store/actions/player';
  import { SCREEN_EVENT_INTERVAL_SECS, DOWNTIME_CHECK_INTERVAL_MS } from '@/config/constants';
  import { globalIntervalManager } from '@/services/GlobalIntervalManager';
  import { WatchdogManager } from '@/utils/webOsSignage/watchdogManager/watchdogManager';
  import { SET_IS_ONLINE } from '@/store/actions/offline';
  import { logger, sendCrashReport } from '@/utils/screenEventsLogger';
  import {
    handleRestartDevice,
    handleClearCache,
    handleTakeScreenshot,
    handleUpdateFirmware,
    handleUpdateSchedule,
    handleChangeTimezone,
  } from '@/utils/screenEventsHandler';
  import { recordDowntime } from '@/utils/screenDowntime';

  window.jQuery = require('jquery');
  require('./plugins/jquery.textfill.min');

  const default_layout = 'dashboard';

  export default {
    name: 'app',
    components: { DownloadingMediaAssetLoader, RefreshTokenText, PlayerModal },
    data() {
      return {
        initializedAsWebOSVersion: null,
        initialRefreshTokenLoading: true,
        isSynchronizingMediaAssets: false,
        isLoadingFonts: false,
      };
    },

    setup() {
      setUniqueScreenCode();
    },

    methods: {
      async fetchOrganisationData() {
        try {
          await this.$store.dispatch('fetchOrganisation');
        } catch (error) {
          console.error('Error fetching organisation data:', error);
        }
      },

      createManagedInterval(id, callback, delay) {
        globalIntervalManager.createSafeInterval(id, callback, delay, {
          maxRetries: 5,
          retryDelay: 2000,
          enableDriftDetection: true,
          driftThreshold: 3,
          onError: (error, attempt) => {
            logger.addLog(`[${id}] Error on attempt ${attempt}: ${error.message || 'NA'}`);
          },
          onRestart: () => {
            logger.addLog(`[${id}] Interval restarted after max retries exceeded`);
          },
        });
      },

      async startWatchdog() {
        // watchdog code don't support webos 3.0 and 3.1 for now
        logger.addLog(`Start running watchdog with version: ${this.initializedAsWebOSVersion}`);
        if (
          this.initializedAsWebOSVersion &&
          this.initializedAsWebOSVersion !== '3.0' &&
          this.initializedAsWebOSVersion !== '3.1'
        ) {
          await WatchdogManager.runOnce(this.initializedAsWebOSVersion);
          this.createManagedInterval(
            'watchdog',
            async () => {
              logger.addLog('Running watchdog interval');
              await WatchdogManager.runOnce(this.initializedAsWebOSVersion);
            },
            1000 * 60 * 5,
          ); // 5 minutes
        }
      },

      async startSynchronizingMediaAssets() {
        if (window.isWebOS && this.isSynchronizingMediaAssets === false) {
          logger.addLog('Start synchronizing media assets');

          this.isSynchronizingMediaAssets = true;

          const maxRetries = 3;
          var retryCount = 0;
          var allFileSynced = false;

          while (!allFileSynced && retryCount < maxRetries) {
            allFileSynced = await FileSynchronizationManager.runFileSynchronizationOnce(
              this.initializedAsWebOSVersion,
            );

            if (!allFileSynced) {
              const exponentialBackoffDelay = Math.pow(retryCount, 2) * 1000;
              await new Promise((resolve) => {
                setTimeout(resolve, exponentialBackoffDelay);
              });

              ++retryCount;
            }
          }

          this.isSynchronizingMediaAssets = false;
          logger.addLog('Media assets synchronized');
        } else if (window.isAndroid) {
          this.isSynchronizingMediaAssets = true;
          logger.addLog('Start synchronizing media assets for Android');
          try {
            await downloadVideosForAndroid();
          } catch (error) {
            logger.addLog(`Error synchronizing media assets for Android: ${error}`);
          } finally {
            this.isSynchronizingMediaAssets = false;
          }
          logger.addLog('Media assets synchronized for Android');
        } else if (window.isTizen) {
          this.isSynchronizingMediaAssets = true;
          logger.addLog('Start synchronizing media assets for Tizen');
          try {
            await downloadMediaForTizen();
          } catch (error) {
            logger.addLog(`Error synchronizing media assets for Tizen: ${error}`);
          } finally {
            this.isSynchronizingMediaAssets = false;
          }
          logger.addLog('Media assets synchronized for Tizen');
        }
      },

      async startLoadingFonts() {
        logger.addLog('Loading fonts started');
        this.isLoadingFonts = true;
        try {
          await loadPicflowFonts();
        } catch (error) {
          logger.addLog(`Error: load fonts, ${error}`);
        }
        this.isLoadingFonts = false;
        logger.addLog('Loading fonts completed');
      },

      async refreshToken() {
        try {
          if (isLoggedIn()) {
            logger.addLog('Refresh token called');
            const token = await refreshAuthToken();
            localStorage.setItem(SESSION_TOKEN_ENUM, token['session_token']);
            localforage.setItem(SESSION_TOKEN_ENUM, token['session_token']);
            logger.addLog('Refresh token completed');
          }
        } catch (err) {
          logger.addLog(`ERROR: Refresh token - Message: ${err.message || 'NA'}}`);
        } finally {
          this.initialRefreshTokenLoading = false;
        }
      },

      async checkScreenStatus() {
        try {
          if (!isLoggedIn()) return;
          logger.addLog('Screen status check started');
          const screenEvents = await getScreenEvents();
          await this.handleScreenEvents(screenEvents);
        } catch (err) {
          logger.addLog(`ERROR: Screen status check - Message: ${err.message || 'NA'}`);
        }
      },

      async handleScreenEvents(screenEvents) {
        try {
          logger.addLog('Handle screen events started');
          localStorage.setItem(WATCHDOG_ENABLED, screenEvents.enable_watchdog);
          // Logout only if screen code does not match. Needed to prevent running multiple screens from same CMS screen
          if (screenEvents.logout && !screenEvents.code_match) {
            logger.addLog('Logging out screen. Expected Logout.');
            localStorage.setItem(EXPECTED_LOGOUT, 'true');
            await saveScreenEvents({ logout: false });
            await logger.sendLogs();
            await clearStorage();
            if (window.isWebOS === true) {
              await FileSynchronizationManager.removeAllInternalFiles();
            } else if (window.isAndroid === true) {
              // reloadPage first as window.logout has an issue that blocks further execution
              // reloadPage is async so window.logout call to APK will happen regardless
              reloadPage();
              window.logout();
            }
            reloadPage();
          }

          // If screen code does not match, it is an invalid screen and therefore ignoring rest of the events.
          if (!screenEvents.code_match) return;

          if (screenEvents.publish || screenEvents.refresh) {
            logger.addLog('Publishing/Refreshing screen');
            await saveScreenEvents({
              ...(screenEvents.publish && { publish: false }),
              ...(screenEvents.refresh && { refresh: false }),
            });
            const screenId = localStorage.getItem(SCREEN_ID_ENUM);
            const result = await fetchScreenLayout(screenId);
            if (result.success && result.data.status === 200) {
              localStorage.setItem(SCREEN_LAYOUT_ENUM, result.data.data.layout_id);
              localforage.setItem(SCREEN_LAYOUT_ENUM, result.data.data.layout_id);
            }
            await logger.sendLogs();
            reloadPage();
          }

          if (screenEvents.orientation) {
            // Saved orientation information to force correct the orientation in android devices from App.vue on subsequent app restarts.
            localStorage.setItem(PLAYER_ORIENTATION, screenEvents.orientation);
          }

          if (screenEvents.change_orientation && screenEvents.orientation) {
            logger.addLog('Changing Screen orientation');
            await saveScreenEvents({ changeOrientation: false });
            if (window.isTizen) {
              const orientationChanged = changeTizenOrientation(screenEvents.orientation);
              if (orientationChanged) {
                logger.addLog('Orientation changed successfully. Rebooting the device');
                await logger.sendLogs();
                restartTizenScreen();
              } else {
                logger.addLog('Orientation change failed for Tizen device');
              }
            } else {
              const cb = () => {
                if (
                  screenEvents.orientation === SCREEN_ORIENTATION.LANDSCAPE ||
                  screenEvents.orientation === SCREEN_ORIENTATION.REVERSE_LANDSCAPE
                ) {
                  this.$store.commit(SET_PLAYER_ROTATION, true);
                } else {
                  this.$store.commit(SET_PLAYER_ROTATION, false);
                }
              };

              if (window.changeOrientation) {
                window.changeOrientation(screenEvents.orientation, cb);
              } else {
                cb();
              }
            }
          }

          if (screenEvents.restart_device) {
            logger.addLog('Restarting device');

            await this.clearRestartFlag();
            logger.addLog('Initiating device restart from backend command...');
            await logger.sendLogs();

            handleRestartDevice();
          }

          if (screenEvents.take_screenshot) {
            logger.addLog('Screenshot requested by backend');

            await saveScreenEvents({ takeScreenshot: false });

            handleTakeScreenshot();
          }

          if (screenEvents.update_firmware) {
            logger.addLog('Firmware update requested by backend');

            await saveScreenEvents({ updateFirmware: false });

            handleUpdateFirmware(screenEvents.firmware_details);
          }

          if (screenEvents.clear_cache) {
            logger.addLog('Clear cache requested by backend');

            await saveScreenEvents({ clearCache: false });

            handleClearCache();
          }

          if (screenEvents.update_schedule) {
            logger.addLog('Update schedule requested by backend');

            await saveScreenEvents({ updateSchedule: false });
            await handleUpdateSchedule(screenEvents.schedule_details);
          }

          if (screenEvents.change_timezone) {
            logger.addLog('Change timezone requested by backend');

            await saveScreenEvents({ changeTimezone: false });
            await handleChangeTimezone(screenEvents.timezone);
          }

          logger.addLog('Handle screen events completed');
        } catch (err) {
          logger.addLog(`ERROR: Handle screen events - Message: ${err.message || 'NA'}`);
        }
      },

      updateOnlineStatus() {
        logger.addLog('Online status updated to: ' + navigator.onLine);
        this.$store.commit(SET_IS_ONLINE, navigator.onLine);

        if (navigator.onLine && isLoggedIn()) {
          this.fetchOrganisationData();
        }
      },

      startOfflineCheck() {
        this.updateOnlineStatus();
      },

      async runDeviceInfo() {
        if (!isLoggedIn()) return;
        logger.addLog('runDeviceInfo called on interval');
        try {
          if (window.isWebOS) {
            const deviceInfo = await getWebOsDeviceInfo();
            await saveDeviceInfo(deviceInfo);
          } else if (window.isTizen) {
            const deviceInfo = await getTizenDeviceInfo();
            await saveDeviceInfo(deviceInfo);
          } else if (window.getDeviceInfo) {
            // android and web
            window.getDeviceInfo();
          }
          logger.addLog('runDeviceInfo completed');
        } catch (err) {
          logger.addLog(`ERROR: runDeviceInfo - Message: ${err.message}`);
        }
      },

      async handleDeviceInfo(deviceInfo) {
        logger.addLog('Device info save called');
        try {
          await saveDeviceInfo(deviceInfo);
          if (window.isAndroid) {
            const orientation = localStorage.getItem(PLAYER_ORIENTATION);
            this.applyAndroidOrientation(orientation);
          }
        } catch (error) {
          logger.addLog(`ERROR: handleDeviceInfo - Message: ${error.message || 'NA'}`);
        }
      },

      applyAndroidOrientation(orientation) {
        // Android has an issue where the player auto rotates to landscape. This logic tries to fix that.
        // Also, sometimes deviceInfo.orientation === -1 in which case the orientation will not be set in local storage and
        // screen will be rotated to match layout orientation
        try {
          if (!window.changeOrientation) {
            logger.addLog('changeOrientation function not available on window');
            return;
          }
          if (orientation) {
            logger.addLog(
              `changeOrientation called and orientation is manually set to ${orientation}`,
            );
            window.changeOrientation(orientation);
          } else {
            const layout = this.$store.state.layout;
            if (!layout || !layout.settings) {
              logger.addLog('No layout info found in store');
              return;
            }
            const layoutOrientation = layout.settings.isHorizontal
              ? SCREEN_ORIENTATION.LANDSCAPE
              : SCREEN_ORIENTATION.PORTRAIT;
            logger.addLog(
              `changeOrientation called from applyAndroidOrientation to ${layoutOrientation}`,
            );
            window.changeOrientation(layoutOrientation);
          }
        } catch (error) {
          logger.addLog(`ERROR: applyAndroidOrientation - Message: ${error.message || 'NA'}`);
        }
      },

      setAndroidInterface() {
        if (!window.isAndroid) {
          logger.addLog('Screenshot capture not available - not Android platform');
          return;
        }

        // Set up global callback functions for Android
        window.orientationChanged = this.orientationChanged;
      },

      async clearRestartFlag() {
        try {
          if (isLoggedIn()) {
            logger.addLog('Clearing restart flag to prevent restart loops');

            await saveScreenEvents({ restartDevice: false });

            logger.addLog('Restart flag cleared successfully');
          }
        } catch (error) {
          logger.addLog(`ERROR: Failed to clear restart flag: ${error.message || 'Unknown error'}`);
        }
      },

      orientationChanged(screenOrientation) {
        // TODO: Implement orientation changed logic
      },
    },

    computed: {
      layout() {
        return (this.$route.meta.layout || default_layout) + '-layout';
      },

      isAppOnline() {
        return this.$store.state.offline.isAppOnline;
      },

      showPlayerModal() {
        return this.$store.state.player.showPlayerModal;
      },
    },

    async created() {
      try {
        await sleepIfNetworkSpikeRange();

        window.setDeviceInfo = this.handleDeviceInfo;
        await this.refreshToken();

        this.startOfflineCheck();

        // Clear restart flag BEFORE setting up screen status monitoring to prevent immediate restart
        if (isLoggedIn()) {
          await this.clearRestartFlag();
        }

        this.createManagedInterval(
          'refresh-token',
          async () => {
            await this.refreshToken();
          },
          1000 * 60 * 10,
        );

        this.createManagedInterval(
          'screen-status-monitor',
          this.checkScreenStatus,
          1000 * SCREEN_EVENT_INTERVAL_SECS,
        );

        this.createManagedInterval(
          'device-info',
          async () => {
            await this.runDeviceInfo();
          },
          1000 * 60 * 5,
        );

        this.createManagedInterval(
          'logger',
          () => {
            logger.sendLogs();
          },
          1000 * 60 * 5,
        );

        this.createManagedInterval(
          'downtime-reporter',
          async () => {
            await recordDowntime();
          },
          DOWNTIME_CHECK_INTERVAL_MS,
        );
      } catch (error) {
        logger.addLog(`ERROR: created - Message: ${error.message || 'NA'}`);
      }
    },

    async mounted() {
      try {
        window.addEventListener('online', this.updateOnlineStatus);
        window.addEventListener('offline', this.updateOnlineStatus);

        // should only run in production
        if (
          config.sentry.startSessionReplayOnMounted &&
          (window.isWebOS === true || window.isAndroid === true) &&
          this.isAppOnline
        ) {
          PicflowSentryPlugin.dispatchStartSessionReplayEvent();
        }

        if (window.isWebOS === true) {
          try {
            this.initializedAsWebOSVersion = await webOSAppInitialization();
            logger.addLog(
              'WebOS initialization completed with version: ' + this.initializedAsWebOSVersion,
            );
            await this.startWatchdog();
          } catch (error) {
            logger.addLog('ERROR: WebOS initialization failed: ' + error);
          }
        }

        if (window.isAndroid) {
          await tryAndroidInterfaceInit();
          const orientation = localStorage.getItem(PLAYER_ORIENTATION);
          if (window.changeOrientation && orientation) {
            window.changeOrientation(orientation);
          }
          const screenId = localStorage.getItem(SCREEN_ID_ENUM);
          if (window.setScreenId && screenId) {
            window.setScreenId(screenId);
          }
          window.logCrashReport = sendCrashReport;

          // Initialize commands for Android devices
          this.setAndroidInterface();
        }

        // Wait for refreshToken (in created()) to complete before making any API calls
        while (this.initialRefreshTokenLoading) {
          await new Promise((resolve) => setTimeout(resolve, 100));
        }

        if (isLoggedIn()) {
          await this.startSynchronizingMediaAssets();
          // load fonts need to be after startSynchronizingMediaAssets
          await this.startLoadingFonts();
          await this.fetchOrganisationData();
          await this.runDeviceInfo();
          await sendScreenRefreshEvent();
        }
      } catch (error) {
        logger.addLog(`ERROR: mounted - Message: ${error.message || 'NA'}`);
      }
    },

    beforeDestroy() {
      logger.addLog('App destroyed, clearing timers and removing listeners');
      globalIntervalManager.stopInterval('refresh-token');
      globalIntervalManager.stopInterval('screen-status-monitor');
      globalIntervalManager.stopInterval('device-info');
      globalIntervalManager.stopInterval('watchdog');
      globalIntervalManager.stopInterval('logger');
      globalIntervalManager.stopInterval('downtime-reporter');

      window.removeEventListener('online', this.updateOnlineStatus);
      window.removeEventListener('offline', this.updateOnlineStatus);
    },
  };
</script>
